
import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import Header from './components/Header';
import ProductCard from './components/ProductCard';
import CartDrawer from './components/CartDrawer';
import ProductDetailView from './components/ProductDetailView';
import AIAssistant from './components/AIAssistant';
import { PRODUCTS, CATEGORIES } from './constants';
import { Product, CartItem, CategoryType } from './types';
import { getCategorySummary } from './services/gemini';
import { useLiveAmazonData } from './hooks/useLiveAmazonData';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './services/firebase';
import AdminAddProduct from './components/AdminAddProduct';

const handleFirestoreError = (error: unknown, operationType: string, path: string | null) => {
  console.warn(`Firestore Error [${operationType}] on path [${path}]:`, error);
};

const App: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CategoryType>('All');
  const [sortBy, setSortBy] = useState('Featured');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isAIOpen, setIsAIOpen] = useState(false);
  const [isMobileRangeOpen, setIsMobileRangeOpen] = useState(false);
  const [categorySummary, setCategorySummary] = useState<string | null>(null);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [hasKey, setHasKey] = useState<boolean | null>(null);
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);

  const [dbProducts, setDbProducts] = useState<Product[]>([]);
  const [isDbLoading, setIsDbLoading] = useState(true);

  useEffect(() => {
    const fetchAmazonProducts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'amazonProducts'));
        const products: Product[] = [];
        querySnapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          products.push({
            id: data.id,
            name: data.name,
            category: data.category,
            price: Number(data.price),
            description: data.description,
            image: data.image,
            tags: data.tags || [],
            url: data.url,
            inStock: data.inStock,
            asin: data.asin,
            company: data.company || 'Amazon',
            rating: data.rating || null,
            reviewCount: data.reviewCount || null
          });
        });
        
        if (products.length > 0) {
          setDbProducts(products);
        } else {
          const staticAmazonProducts = PRODUCTS.filter(p => p.asin && p.asin.trim() !== '');
          setDbProducts(staticAmazonProducts);
        }
      } catch (err) {
        handleFirestoreError(err, 'get', 'amazonProducts');
        const staticAmazonProducts = PRODUCTS.filter(p => p.asin && p.asin.trim() !== '');
        setDbProducts(staticAmazonProducts);
      } finally {
        setIsDbLoading(false);
      }
    };

    fetchAmazonProducts();
  }, []);

  const mergedProducts = useMemo(() => {
    const nonAmazon = PRODUCTS.filter(p => !p.asin || p.asin.trim() === '');
    return [...nonAmazon, ...dbProducts];
  }, [dbProducts]);

  const { liveData } = useLiveAmazonData(mergedProducts);

  // High-resolution photo provided by the user
  const LYNSEY_PHOTO = "https://images.squarespace-cdn.com/content/6962244a930d3931ee04d503/d5e209d2-8a40-498c-805b-924c27c3cad5/Screenshot+2026-01-14+at+17.21.19.png?content-type=image%2Fpng";

  const isEmbedded = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('embed') === 'true';
  }, []);

  useEffect(() => {
    const checkKey = async () => {
      // @ts-ignore
      if (window.aistudio && typeof window.aistudio.hasSelectedApiKey === 'function') {
        // @ts-ignore
        const selected = await window.aistudio.hasSelectedApiKey();
        setHasKey(selected);
      } else {
        setHasKey(true);
      }
    };
    checkKey();
  }, []);

  useLayoutEffect(() => {
    // Force immediate scroll to top when opening detail view
    if (quickViewProduct) {
      window.scrollTo(0, 0);
      // Extra precaution for browsers that might have async layout shifts
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  }, [quickViewProduct]);

  const handleSelectKey = async () => {
    // @ts-ignore
    if (window.aistudio && typeof window.aistudio.openSelectKey === 'function') {
      // @ts-ignore
      await window.aistudio.openSelectKey();
      setHasKey(true);
    }
  };

  const filteredProducts = useMemo(() => {
    let result = mergedProducts.filter(p => {
      const matchesSearch = (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                            (p.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || p.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });

    switch (sortBy) {
      case 'Price: Low to High':
        result = [...result].sort((a, b) => a.price - b.price);
        break;
      case 'Price: High to Low':
        result = [...result].sort((a, b) => b.price - a.price);
        break;
      case 'Best Rated':
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      default:
        break;
    }

    return result;
  }, [searchTerm, selectedCategory, sortBy]);

  const handleCategoryAction = async (category: CategoryType) => {
    setSelectedCategory(category);
    setIsMobileRangeOpen(false); // Close mobile range on selection
    setIsSummarizing(true);
    setCategorySummary(null);
    try {
      const summary = await getCategorySummary(category, mergedProducts);
      setCategorySummary(summary);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSummarizing(false);
    }
  };

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    setIsCartOpen(true);
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const currentPath = window.location.pathname;
  if (currentPath === '/admin/add-product' || currentPath === '/admin/add-product/') {
    return <AdminAddProduct />;
  }

  if (hasKey === false) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-2xl p-10 text-center border border-[#F1B1BA]/20 animate-fadeIn">
          <div className="w-24 h-24 bg-[#F1B1BA]/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <i className="fas fa-sparkles text-4xl text-[#F1B1BA]"></i>
          </div>
          <h1 className="text-3xl font-black text-[#5C3E43] mb-4 uppercase tracking-tighter">Unlock Expert Mode</h1>
          <p className="text-slate-500 mb-8 text-sm leading-relaxed">
            To use Lynsey's AI advice and real-time summaries, please select an API key from a paid GCP project.
          </p>
          <button 
            onClick={handleSelectKey}
            className="w-full py-4 bg-[#F1B1BA] text-[#5C3E43] rounded-2xl font-black shadow-lg shadow-pink-900/10 hover:brightness-105 transition-all uppercase tracking-widest text-sm mb-4"
          >
            Select API Key
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col relative overflow-x-hidden">
      <CartDrawer 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)}
        items={cart}
        onUpdateQuantity={updateQuantity}
        onRemove={removeFromCart}
      />

      <div className="flex-1 flex flex-col bg-white">
        {!isEmbedded && <Header />}
        
        {quickViewProduct ? (
          <ProductDetailView 
            product={quickViewProduct} 
            onClose={() => setQuickViewProduct(null)} 
          />
        ) : (
          <main className={`max-w-[1360px] mx-auto w-full animate-fadeIn ${isEmbedded ? 'px-2 py-4' : 'px-4 pt-6 pb-4'}`}>
            {!isEmbedded && (
              <section className="mb-8 relative overflow-hidden rounded-[2rem] bg-[#F1B1BA] p-6 md:p-10 shadow-2xl min-h-[250px] md:min-h-[400px] flex items-center">
                <div className="relative z-20 max-w-2xl">
                  <span className="inline-block px-3 py-1 bg-white/30 backdrop-blur-md rounded-full text-[9px] font-black uppercase tracking-[0.2em] mb-4 text-[#5C3E43] border border-white/20">
                    Recommended Products
                  </span>
                  <h2 className="text-4xl md:text-6xl font-black mb-4 md:mb-5 leading-[0.95] text-white drop-shadow-lg">
                    Queen of Clean <br className="hidden md:block" /> Store
                  </h2>
                  <div className="text-base md:text-lg text-white font-medium max-w-md leading-relaxed opacity-90">
                    <p>Real-life favourites from me. Only products I genuinely use, love and recommend.</p>
                    <p className="mt-3 font-black opacity-100 text-xl md:text-2xl uppercase tracking-widest border-t border-white/20 pt-3 inline-block">Tried. Tested. Loved</p>
                  </div>
                </div>

                <div className="hidden lg:flex absolute right-12 top-0 bottom-0 items-center justify-end pointer-events-none z-10 w-1/2">
                   <div className="flex flex-row items-center gap-6 scale-90 xl:scale-100">
                      <div className="transition-all duration-700 hover:scale-105 pointer-events-auto">
                        <img 
                          src="https://m.media-amazon.com/images/I/81a4bum7NxL._SY522_.jpg" 
                          alt="The 15-Minute Clean" 
                          className="h-56 w-auto object-contain drop-shadow-[0_25px_40px_rgba(0,0,0,0.2)] animate-float-slow"
                        />
                      </div>
                      <div className="transition-all duration-700 hover:scale-105 pointer-events-auto">
                        <img 
                          src="https://m.media-amazon.com/images/I/61Di4FTimSL._SY522_.jpg" 
                          alt="How To Clean Your House" 
                          className="h-56 w-auto object-contain drop-shadow-[0_25px_40px_rgba(0,0,0,0.2)] animate-float-medium"
                        />
                      </div>
                      <div className="transition-all duration-700 hover:scale-105 pointer-events-auto">
                        <img 
                          src="https://m.media-amazon.com/images/I/71pWngfbCoL._SY522_.jpg" 
                          alt="The Easy Life" 
                          className="h-56 w-auto object-contain drop-shadow-[0_25px_40px_rgba(0,0,0,0.2)] animate-float-fast"
                        />
                      </div>
                   </div>
                </div>

                <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
                    <div className="absolute w-[500px] h-[500px] bg-white/10 rounded-full blur-[100px] -top-1/4 -right-1/4"></div>
                    <div className="absolute w-[300px] h-[300px] bg-white/5 rounded-full blur-[60px] bottom-0 left-1/4"></div>
                    <div className="absolute top-[10%] right-[15%] text-white/30 animate-pulse">
                      <i className="fas fa-sparkles text-4xl"></i>
                    </div>
                </div>
              </section>
            )}

            <div className="flex flex-col md:flex-row gap-8">
              <aside className="hidden md:block w-64 shrink-0">
                <div className="sticky top-6">
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 px-2">Browse Range</h3>
                  <div className="space-y-1 max-h-[calc(100vh-120px)] overflow-y-auto pr-2 custom-scrollbar">
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat}
                        onClick={() => handleCategoryAction(cat as CategoryType)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-bold text-sm transition-all group ${
                          selectedCategory === cat 
                          ? 'bg-[#F1B1BA] text-[#5C3E43] shadow-lg' 
                          : 'bg-transparent text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <span className="text-left leading-tight">{cat}</span>
                        <i className={`fas fa-chevron-right text-[10px] transition-transform shrink-0 ml-2 ${selectedCategory === cat ? 'translate-x-0' : '-translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'}`}></i>
                      </button>
                    ))}
                  </div>
                </div>
              </aside>

              <div className="flex-1">
                <div className="bg-white pb-6 pt-2">
                  <div className="flex flex-col space-y-4">
                    <div className={`relative animate-slideUp ${isAIOpen ? 'z-[9999]' : 'z-20'}`}>
                      <button 
                        onClick={() => setIsAIOpen(!isAIOpen)}
                        className="w-full flex items-center justify-between p-5 bg-[#F1B1BA]/10 border border-[#F1B1BA]/30 rounded-2xl hover:bg-[#F1B1BA]/20 transition-all group shadow-sm hover:shadow-md"
                      >
                         <div className="flex items-center space-x-4">
                            <div className="w-14 h-14 rounded-full border-2 border-white overflow-hidden shadow-md shrink-0 bg-white">
                               <img src={LYNSEY_PHOTO} alt="Lynsey" className="w-full h-full object-cover" style={{ imageRendering: '-webkit-optimize-contrast' }} />
                            </div>
                            <div className="text-left">
                               <p className="text-[10px] font-black text-[#F1B1BA] uppercase tracking-widest leading-none mb-1.5">Expert Homecare Advice</p>
                               <h4 className="text-sm md:text-base font-black text-[#5C3E43] leading-none">Need cleaning tips? Ask Lynsey...</h4>
                            </div>
                         </div>
                         <div className="flex items-center space-x-2 px-4 py-2 bg-[#F1B1BA] text-[#5C3E43] rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg border border-white/50">
                           <span>{isAIOpen ? 'Close' : 'Ask Now'}</span>
                           <i className={`fas ${isAIOpen ? 'fa-times' : 'fa-sparkles'} text-[9px]`}></i>
                         </div>
                      </button>
                      <AIAssistant products={mergedProducts} isOpen={isAIOpen} onToggle={setIsAIOpen} />
                    </div>

                    <div className="relative group z-10">
                      <div className="flex flex-col space-y-2">
                        <label htmlFor="product-search" className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] px-1">
                          Find your professional solution
                        </label>
                        <div className="relative">
                          <input
                            id="product-search"
                            type="text"
                            placeholder="Search Lynsey's professional range..."
                            className="w-full pl-14 pr-4 py-4 md:py-5 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-[#F1B1BA] focus:border-transparent transition-all text-sm font-semibold shadow-sm group-hover:shadow-md"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <i className="fas fa-search absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#F1B1BA] transition-colors text-lg"></i>
                        </div>
                      </div>

                      <div className="md:hidden mt-4">
                        <button 
                          onClick={() => setIsMobileRangeOpen(!isMobileRangeOpen)}
                          className="w-full flex items-center justify-between px-5 py-4 bg-white border border-[#F1B1BA]/30 rounded-2xl text-slate-600 font-bold text-sm shadow-sm active:bg-[#F1B1BA]/5"
                        >
                          <div className="flex items-center space-x-3">
                            <i className="fas fa-bars-staggered text-[#F1B1BA]"></i>
                            <span className="uppercase tracking-widest text-[11px] font-black">Browse Range</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-[10px] font-black text-[#F1B1BA] bg-[#F1B1BA]/10 px-2 py-1 rounded-lg uppercase truncate max-w-[100px]">{selectedCategory}</span>
                            <i className={`fas fa-chevron-${isMobileRangeOpen ? 'up' : 'down'} text-[10px] text-slate-300`}></i>
                          </div>
                        </button>
                        
                        {isMobileRangeOpen && (
                          <div className="mt-2 bg-slate-50 rounded-2xl p-4 border border-slate-100 flex flex-wrap gap-2 animate-fadeIn max-h-[300px] overflow-y-auto">
                            {CATEGORIES.map(cat => (
                              <button
                                key={cat}
                                onClick={() => handleCategoryAction(cat as CategoryType)}
                                className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                  selectedCategory === cat 
                                  ? 'bg-[#F1B1BA] text-[#5C3E43] shadow-md ring-2 ring-[#F1B1BA]/20' 
                                  : 'bg-white text-slate-500 border border-slate-200 hover:border-[#F1B1BA]/40 shadow-sm'
                                }`}
                              >
                                {cat}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {(isSummarizing || categorySummary) && (
                  <div className="mb-6 p-6 bg-white rounded-2xl border-l-4 border-[#F1B1BA] shadow-sm animate-fadeIn">
                    <div className="flex items-center space-x-2 mb-2">
                      <i className="fas fa-magic text-[#F1B1BA] text-sm"></i>
                      <span className="text-[10px] font-black text-[#F1B1BA] uppercase tracking-widest">Curator's Note</span>
                    </div>
                    {isSummarizing ? (
                      <div className="flex flex-col space-y-2 animate-pulse">
                        <div className="h-3 bg-slate-100 rounded w-full"></div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-600 italic leading-relaxed">"{categorySummary}"</p>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                  <h3 className="text-xl font-black text-[#5C3E43]">
                    {selectedCategory} <span className="text-slate-300 font-medium ml-2 text-sm">({filteredProducts.length} Items)</span>
                  </h3>
                  <div className="flex items-center space-x-2 text-[10px] font-black text-slate-400 bg-white px-3 py-2 rounded-xl border border-slate-100 shadow-sm uppercase tracking-wider">
                    <span>Sort by:</span>
                    <select 
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="bg-transparent text-[#5C3E43] border-none focus:ring-0 cursor-pointer font-black focus:outline-none pr-6"
                    >
                      <option value="Featured">Featured</option>
                      <option value="Price: Low to High">Price: Low to High</option>
                      <option value="Price: High to Low">Price: High to Low</option>
                      <option value="Best Rated">Best Rated</option>
                    </select>
                  </div>
                </div>

                {filteredProducts.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-6">
                    {filteredProducts.map(product => (
                      <ProductCard 
                        key={product.id} 
                        product={product} 
                        onAddToCart={addToCart} 
                        onQuickView={(p) => setQuickViewProduct(p)}
                        liveData={product.asin ? liveData[product.asin] : undefined}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center bg-white rounded-3xl border border-slate-100 shadow-inner mb-6">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-search text-2xl text-slate-200"></i>
                    </div>
                    <h3 className="text-xl font-black text-[#F1B1BA] uppercase tracking-tighter">No results found</h3>
                  </div>
                )}
              </div>
            </div>

            {!isEmbedded && (
              <footer className="bg-white border-t border-slate-100 pt-8 pb-12 mt-12">
                <div className="max-w-[1360px] mx-auto px-4 text-center flex flex-col items-center">
                  <h2 className="text-2xl font-black text-[#F1B1BA] uppercase tracking-tighter">Lynsey's Best Buys</h2>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">Recommended Products & Expert Homecare</p>
                </div>
              </footer>
            )}
          </main>
        )}
      </div>
      <style>{`
        @keyframes float-slow {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        @keyframes float-medium {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
          100% { transform: translateY(0px); }
        }
        @keyframes float-fast {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
          100% { transform: translateY(0px); }
        }
        .animate-float-slow { animation: float-slow 6s ease-in-out infinite; }
        .animate-float-medium { animation: float-medium 7s ease-in-out infinite; }
        .animate-float-fast { animation: float-fast 5s ease-in-out infinite; }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #F1B1BA;
        }
      `}</style>
    </div>
  );
};

export default App;
